/**
 * 霓虹蛇境 (Neon Snake Realm) - 键盘输入处理 Hook
 *
 * 本 Hook 从 App.tsx 中提取了键盘事件监听逻辑，
 * 封装为独立、可复用的自定义 Hook。
 *
 * 职责：
 * - 监听全局键盘按下事件
 * - 将方向键（↑↓←→）和 WASD 键映射为游戏方向指令
 * - 在 Idle/GameOver 状态下，任意方向键启动游戏
 * - 在 Playing 状态下，方向键控制蛇的移动方向
 * - 阻止浏览器默认行为（如页面滚动）
 *
 * 使用方式：
 * ```tsx
 * const [gameState, dispatch] = useReducer(gameReducer, undefined, createInitialState);
 * useKeyboardInput(dispatch, gameState.status);
 * ```
 */

import { useCallback, useEffect } from 'react';
import type { GameAction, Direction } from '@/types/game';
import { GameStatus } from '@/types/game';
import { DIRECTION_VECTORS } from '@/game/constants';

// ============================================================================
// 键盘按键到方向向量的映射表
// ============================================================================

/**
 * 键盘按键到方向向量的完整映射。
 *
 * 支持两套控制方案：
 * - 方向键：ArrowUp、ArrowDown、ArrowLeft、ArrowRight
 * - WASD 键：w/W、s/S、a/A、d/D
 *
 * 映射表在模块顶层定义为常量，避免每次渲染时重新创建对象。
 */
const KEY_TO_DIRECTION: Record<string, Direction> = {
  // 方向键
  ArrowUp: DIRECTION_VECTORS.UP,
  ArrowDown: DIRECTION_VECTORS.DOWN,
  ArrowLeft: DIRECTION_VECTORS.LEFT,
  ArrowRight: DIRECTION_VECTORS.RIGHT,

  // WASD 键（大写）
  W: DIRECTION_VECTORS.UP,
  S: DIRECTION_VECTORS.DOWN,
  A: DIRECTION_VECTORS.LEFT,
  D: DIRECTION_VECTORS.RIGHT,

  // WASD 键（小写）
  w: DIRECTION_VECTORS.UP,
  s: DIRECTION_VECTORS.DOWN,
  a: DIRECTION_VECTORS.LEFT,
  d: DIRECTION_VECTORS.RIGHT,
};

// ============================================================================
// useKeyboardInput Hook
// ============================================================================

/**
 * 键盘输入处理 Hook。
 *
 * 监听全局 keydown 事件，将按键映射为游戏操作。
 * 根据当前游戏状态执行不同逻辑：
 * - **Idle / GameOver**：方向键启动游戏并设置初始方向
 * - **Playing**：方向键更新蛇的移动方向
 *
 * 自动处理事件监听器的注册与清理，避免内存泄漏。
 * 使用 useCallback 缓存事件处理函数，减少不必要的 useEffect 重新执行。
 *
 * @param dispatch - 游戏状态 Reducer 的 dispatch 函数
 * @param gameStatus - 当前游戏运行状态
 *
 * @example
 * ```tsx
 * const [gameState, dispatch] = useReducer(gameReducer, undefined, createInitialState);
 * useKeyboardInput(dispatch, gameState.status);
 * ```
 */
export function useKeyboardInput(
  dispatch: React.Dispatch<GameAction>,
  gameStatus: GameStatus
): void {
  /**
   * 键盘按下事件处理回调。
   *
   * 使用 useCallback 缓存，依赖项为 dispatch 和 gameStatus。
   * 当 gameStatus 变化时（如从 Idle 切换到 Playing），
   * 回调函数会重新创建以反映最新的状态判断逻辑。
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      // 查找按键对应的方向向量
      const direction = KEY_TO_DIRECTION[event.key];

      // 忽略未映射的按键
      if (!direction) {
        return;
      }

      // 阻止浏览器默认行为（如方向键导致的页面滚动）
      event.preventDefault();

      // ---- 状态：等待开始 或 游戏结束 ----
      // 任意方向键启动游戏并设置初始移动方向
      if (
        gameStatus === GameStatus.Idle ||
        gameStatus === GameStatus.GameOver
      ) {
        dispatch({ type: 'START_GAME' });
        dispatch({ type: 'SET_DIRECTION', direction });
        return;
      }

      // ---- 状态：游戏进行中 ----
      // 方向键更新蛇的移动方向
      if (gameStatus === GameStatus.Playing) {
        dispatch({ type: 'SET_DIRECTION', direction });
        return;
      }

      // 其他状态（如 Paused）不响应方向键
    },
    [dispatch, gameStatus]
  );

  /**
   * 注册与清理全局键盘事件监听器。
   *
   * 使用 useEffect 确保：
   * - 组件挂载时注册 keydown 监听器
   * - 组件卸载时移除监听器（防止内存泄漏）
   * - handleKeyDown 变化时重新注册（确保使用最新的回调）
   */
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    // 清理函数：移除事件监听器
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}